import getStartedInviteTeam from '../assets/images/get-started-1-invite-team.jpg';
import getStartedConfirmBrand from '../assets/images/get-started-2-confirm-brand.jpg';
import getStartedConnectDomain from '../assets/images/get-started-3-connect-domain.jpg';
import getStartedConnectAdAccounts from '../assets/images/get-started-4-connect-ad-accounts.jpg';
import getStartedRouteLeads from '../assets/images/get-started-5-route-leads.jpg';
import getStartedConnectSlack from '../assets/images/get-started-6-connect-slack.jpg';
import getStartedAddConnectors from '../assets/images/get-started-7-add-connectors.jpg';
import getStartedPublishFunnel from '../assets/images/get-started-8-publish-funnel.jpg';

export const LAUNCH_CHECKLIST_STEP_IDS = [
  'teammate_invited',
  'brand_added',
  'custom_domain_connected',
  'ad_account_connected',
  'lead_routing_configured',
  'slack_alerts_enabled',
  'agent_tools_connected',
  'landing_page_published',
] as const;

export type LaunchChecklistStepId = (typeof LAUNCH_CHECKLIST_STEP_IDS)[number];

export interface LaunchChecklistStepConfig {
  id: LaunchChecklistStepId;
  title: string;
  description: string;
  primaryAction: string;
  opensSuperAgent?: boolean;
  route: (slug: string) => string;
  learnMoreUrl: string;
  previewImage: string;
}

export const LAUNCH_CHECKLIST_STEP_CONFIGS: LaunchChecklistStepConfig[] = [
  {
    id: 'teammate_invited',
    title: 'Invite your team',
    description:
      'Bring your teammates into the workspace to review, publish, and monitor campaigns with you.',
    primaryAction: 'Invite team',
    route: (slug) => `/workspace/${slug}/settings/organization/members`,
    learnMoreUrl:
      'https://docs.superfunnel.ai/docs/getting-started/team-management',
    previewImage: getStartedInviteTeam,
  },
  {
    id: 'brand_added',
    title: 'Confirm your brand',
    description:
      'Review your logo, colors, and brand rules so generated pages and quizzes feel like your company.',
    primaryAction: 'Confirm brand',
    route: (slug) => `/workspace/${slug}/settings/website/brand-styles`,
    learnMoreUrl:
      'https://docs.superfunnel.ai/docs/getting-started/brand-styles',
    previewImage: getStartedConfirmBrand,
  },
  {
    id: 'custom_domain_connected',
    title: 'Connect your domain',
    description:
      'Use your own domain for published pages and quizzes instead of the default SuperFunnel subdomain.',
    primaryAction: 'Connect domain',
    route: (slug) => `/workspace/${slug}/settings/website/domains`,
    learnMoreUrl: 'https://docs.superfunnel.ai/docs/getting-started/domains',
    previewImage: getStartedConnectDomain,
  },
  {
    id: 'ad_account_connected',
    title: 'Connect your ad accounts',
    description:
      'Connect Google Ads or Meta so SuperFunnel can read campaign structure and link traffic to pages.',
    primaryAction: 'Connect ad accounts',
    route: (slug) => `/workspace/${slug}/settings/integrations/ad-accounts`,
    learnMoreUrl:
      'https://docs.superfunnel.ai/docs/integrations/google-tag-manager',
    previewImage: getStartedConnectAdAccounts,
  },
  {
    id: 'lead_routing_configured',
    title: 'Route leads',
    description:
      'Send new leads and form events to HubSpot, webhooks, or Zapier so follow-up starts outside SuperFunnel.',
    primaryAction: 'Route leads',
    route: (slug) => `/workspace/${slug}/settings/integrations/webhooks`,
    learnMoreUrl: 'https://docs.superfunnel.ai/docs/integrations/webhooks',
    previewImage: getStartedRouteLeads,
  },
  {
    id: 'slack_alerts_enabled',
    title: 'Connect Slack',
    description:
      'Send high-intent lead and workflow updates to Slack so your team can act quickly.',
    primaryAction: 'Connect Slack',
    route: (slug) => `/workspace/${slug}/settings/integrations/apps`,
    learnMoreUrl: 'https://docs.superfunnel.ai/docs/integrations/slack',
    previewImage: getStartedConnectSlack,
  },
  {
    id: 'agent_tools_connected',
    title: 'Add connectors',
    description:
      'Connect SuperAgent to the apps it needs so it can understand your campaigns, analytics, and customer context.',
    primaryAction: 'Add connectors',
    route: (slug) => `/workspace/${slug}/brain/connectors`,
    learnMoreUrl: 'https://docs.superfunnel.ai/docs/agents/connectors',
    previewImage: getStartedAddConnectors,
  },
  {
    id: 'landing_page_published',
    title: 'Publish a funnel',
    description:
      'Ask SuperAgent to build and publish your first funnel so visitors can see it and analytics can start collecting.',
    primaryAction: 'Open SuperAgent',
    opensSuperAgent: true,
    route: (slug) => `/workspace/${slug}/superagent`,
    learnMoreUrl: 'https://docs.superfunnel.ai/docs/pages/create-page',
    previewImage: getStartedPublishFunnel,
  },
];
